import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useProducts } from '../hooks/useProducts';
import ProductGrid from '../components/products/ProductGrid';
import { ChevronDown, Grid } from 'lucide-react';

const ProductsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('name-asc');
  
  // Fetch products from JSON
  const { products, loading, error } = useProducts();

  // Get URL parameters
  const categoryParam = searchParams.get('category');
  const subcategoryParam = searchParams.get('subcategory');
  const searchParam = searchParams.get('search');

  // Check if category or subcategory is a custom option and redirect to contact page
  useEffect(() => {
    // Check if main category is "custom-furniture"
    const isCustomCategory = categoryParam?.toLowerCase() === 'custom-furniture';
    
    // Check if subcategory is a custom option
    const isCustomSubcategory = subcategoryParam && (
      subcategoryParam.toLowerCase().includes('customized') || 
      subcategoryParam.toLowerCase().includes('custom')
    );
    
    if (isCustomCategory || isCustomSubcategory) {
      // Redirect to contact page with category context
      let categoryName = 'Custom Furniture';
      
      if (isCustomSubcategory && categoryParam) {
        // If it's a custom subcategory, use the parent category name
        categoryName = categoryParam.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      }
      
      navigate(`/contact?inquiry=custom&category=${encodeURIComponent(categoryName)}`, { replace: true });
    }
  }, [subcategoryParam, categoryParam, navigate]);

  // Calculate filtered products directly - don't store in state to avoid stale data
  const filteredProducts = useMemo(() => {
    let result = [...products];

    // Apply category filter from URL
    if (categoryParam) {
      result = result.filter(p => p.category?.toLowerCase() === categoryParam.toLowerCase());
    }

    // Apply subcategory filter from URL
    if (subcategoryParam) {
      result = result.filter(p => {
        const match = p.subcategory?.toLowerCase() === subcategoryParam.toLowerCase();
        return match;
      });
    }

    // Apply search filter from URL
    if (searchParam) {
      const searchLower = searchParam.toLowerCase().trim();
      
      // Split search into individual words for better matching
      const searchWords = searchLower.split(/\s+/).filter(word => word.length > 0);
      
      result = result.filter(p => {
        // Combine all searchable text fields
        const searchableText = [
          p.name || '',
          p.category || '',
          p.subcategory || '',
          p.description || ''
        ].join(' ').toLowerCase();
        
        // Also create a version with hyphens replaced by spaces for better matching
        const normalizedText = searchableText.replace(/-/g, ' ');
        
        // Check if ALL search words are found somewhere in the product
        // This allows "black dresser" to match products with "dresser" in category
        // and "black" in name or description
        const allWordsMatch = searchWords.every(word => 
          normalizedText.includes(word) || searchableText.includes(word)
        );
        
        if (allWordsMatch) {
          return true;
        }
        
        // Fallback: check if the entire phrase matches anywhere
        if (normalizedText.includes(searchLower) || searchableText.includes(searchLower)) {
          return true;
        }
        
        return false;
      });
    }

    // Apply sorting
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => (a.priceCAD || 0) - (b.priceCAD || 0));
        break;
      case 'price-high':
        result.sort((a, b) => (b.priceCAD || 0) - (a.priceCAD || 0));
        break;
      case 'name-asc':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'newest':
      default:
        // newest - sort by ID descending (highest ID = newest)
        result.sort((a, b) => b.id - a.id);
        break;
    }
    
    return result;
  }, [products, categoryParam, subcategoryParam, searchParam, sortBy]);

  // View All Products function
  const handleViewAllProducts = () => {
    navigate('/products');
  };

  // Check if any filters are active
  const hasActiveFilters = categoryParam || subcategoryParam || searchParam;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading products...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading products: {error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-primary-600 to-accent-600 py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4 animate-fade-in">
            {searchParam ? `Search Results for "${searchParam}"` : 
             categoryParam ? categoryParam.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 
             'All Products'}
          </h1>
          <p className="text-white/90 text-lg animate-fade-in" style={{ animationDelay: '0.1s' }}>
            {searchParam ? `Found ${filteredProducts.length} ${filteredProducts.length === 1 ? 'result' : 'results'}` :
             subcategoryParam ? subcategoryParam.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') :
             'Discover our complete collection of affordable furniture'}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col gap-8">
          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="bg-white rounded-lg shadow-soft p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div>
                  <p className="text-dark-800 font-semibold">
                    {filteredProducts.length} {filteredProducts.length === 1 ? 'Product' : 'Products'}
                  </p>
                </div>

                {/* View All Products Button - Only show when filters are active */}
                {hasActiveFilters && (
                  <button
                    onClick={handleViewAllProducts}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors duration-300 text-sm"
                  >
                    <Grid className="w-4 h-4" />
                    View All Products
                  </button>
                )}
              </div>

              {/* Sort Dropdown */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sort by:
                </label>
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none bg-white border-2 border-gray-300 rounded-lg px-4 py-2 pr-10 focus:outline-none focus:border-primary-500 transition-colors duration-300 cursor-pointer"
                  >
                    <option value="newest">Newest</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="name-asc">Name: A to Z</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <ProductGrid products={filteredProducts} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;